// Wash care symbols on a garment: grouped in ISO 3758 order (Washing, Bleaching, Drying, Ironing, Professional care),
// one symbol per group, except that Professional care may hold one dry-clean and one wet-clean symbol together.
// Only the symbols in the organisation's Settings → Database Lists → Wash Care are used; nothing is added here.
import { CARE_GROUPS, careMeaning, type ListEntry } from "./items.js";

export type CareGroup = "washing" | "bleaching" | "drying" | "ironing" | "professional";
export type CareType = "dry" | "wet";
export const CARE_TYPES: Array<[CareType, string]> = [["dry", "Dry clean"], ["wet", "Wet clean"]];
const ORDER: string[] = CARE_GROUPS.map(([k]) => k);

/** The Settings entry for a symbol, matched by its name, ignoring case and spacing (or by its meaning). */
export function findCare(list: ListEntry[], v: string): ListEntry | undefined {
  const k = (s: string) => String(s || "").toLowerCase().replace(/\s+/g, " ").trim();
  return list.find((e) => e.value === v) || list.find((e) => k(e.value) === k(v)) || list.find((e) => !!e.meaning && k(e.meaning) === k(v));
}

/** A symbol's group and (professional care) type, guessed from its name, meaning or picture name — for the update. */
export function inferCare(e: Pick<ListEntry, "value" | "meaning">): { group: CareGroup | ""; type: CareType | "" } {
  const t = `${e.value} ${e.meaning || ""}`.toLowerCase();
  if (/wet[\s-]*clean|\(w\)|professional wet|\bw\b.*circle/.test(t)) return { group: "professional", type: "wet" };
  if (/dry[\s-]*clean|professional|\(p\)|\(f\)|\bperc|hydrocarbon|circle[\s-]*[pf]\b|dryclean/.test(t)) return { group: "professional", type: "dry" };
  if (/bleach|chlorine|oxygen/.test(t)) return { group: "bleaching", type: "" };
  if (/tumble|dry flat|line dry|drip dry|dry in (the )?shade|hang to dry|natural drying|\bdrying\b|do not dry|dryer|drier/.test(t)) return { group: "drying", type: "" };
  if (/\biron|sole[\s-]*plate|steam/.test(t)) return { group: "ironing", type: "" };
  if (/wash|tub|laundr|°c|hand wash|\d+\s*deg/.test(t)) return { group: "washing", type: "" };
  return { group: "", type: "" };
}

const groupOf = (e: ListEntry | undefined): string => (e?.careGroup && ORDER.includes(e.careGroup) ? e.careGroup : "");
const typeOf = (e: ListEntry | undefined): string => (e?.careType === "dry" || e?.careType === "wet" ? e.careType : "");
const nameOf = (list: ListEntry[], v: string) => { const e = findCare(list, v); return e ? careMeaning(e) : v; };

/** Symbols in ISO order (group order, then their order in Settings); symbols with no group come last. */
export function careOrder(symbols: string[], list: ListEntry[]): string[] {
  const rank = (v: string) => {
    const e = findCare(list, v), g = groupOf(e);
    return (g ? ORDER.indexOf(g) : ORDER.length) * 10000 + (e ? list.indexOf(e) : 9999);
  };
  return [...new Set(symbols)].sort((a, b) => rank(a) - rank(b));
}

/** Two symbols that can't be ticked together: same group (Professional care: same type, or either without a type). */
export function careClash(list: ListEntry[], a: string, b: string): boolean {
  if (a === b) return false;
  const ea = findCare(list, a), eb = findCare(list, b);
  const g = groupOf(ea);
  if (!g || g !== groupOf(eb)) return false;
  if (g === "professional") { const ta = typeOf(ea), tb = typeOf(eb); return !ta || !tb || ta === tb; }
  return true;
}

/** The first conflicting pair in a selection, as a plain sentence, or "" when it's fine. */
export function careConflict(symbols: string[], list: ListEntry[]): string {
  for (let i = 0; i < symbols.length; i++) {
    for (let j = i + 1; j < symbols.length; j++) {
      if (careClash(list, symbols[i], symbols[j])) {
        const g = CARE_GROUPS.find(([k]) => k === groupOf(findCare(list, symbols[i])))?.[1] || "";
        return `"${nameOf(list, symbols[i])}" and "${nameOf(list, symbols[j])}" can't be combined: choose one ${g} symbol${g === "Professional care" ? " (one dry-clean and one wet-clean at most)" : ""}.`;
      }
    }
  }
  return "";
}

/** The care text lines made from the symbols: their names (meanings), in ISO order. */
export const careLines = (symbols: string[], list: ListEntry[]): string[] => careOrder(symbols, list).map((v) => nameOf(list, v));

/** A wash care record's text: its own lines, or (none saved) the symbols' names. */
export const recordLines = (symbols: string[], lines: string[] | undefined, list: ListEntry[]): string[] => (lines && lines.length ? lines : careLines(symbols, list));

/** Same symbols and the same text (text made from the symbols counts as the same as no text saved). */
export function sameCare(a: { symbols: string[]; lines?: string[] }, b: { symbols: string[]; lines?: string[] }, list: ListEntry[]): boolean {
  const sa = careOrder(a.symbols || [], list), sb = careOrder(b.symbols || [], list);
  return JSON.stringify(sa) === JSON.stringify(sb) && JSON.stringify(recordLines(sa, a.lines, list)) === JSON.stringify(recordLines(sb, b.lines, list));
}

/** Groups for the garment form: each with its symbols (only the organisation's own, active ones plus any already ticked). */
export function careGroups(list: ListEntry[], ticked: string[] = []): Array<{ key: string; label: string; entries: ListEntry[] }> {
  const shown = list.filter((e) => !e.inactive || ticked.includes(e.value));
  const out = CARE_GROUPS.map(([k, l]) => ({ key: k, label: l, entries: shown.filter((e) => groupOf(e) === k) }));
  out.push({ key: "", label: "Other care symbols", entries: shown.filter((e) => !groupOf(e)) });
  return out.filter((g) => g.entries.length);
}

// ---- Selection groups (Settings → Database Lists → Wash Care): the organisation's own rules --------------------------
/** A group of symbols of which at most `max` can be ticked on a garment (e.g. "Washing temperature · choose 1"). */
export interface CareSelectionGroup { id: string; name: string; max: number }
export function normalizeCareGroups(v: unknown): CareSelectionGroup[] {
  const out: CareSelectionGroup[] = [];
  for (const g of Array.isArray(v) ? v : []) {
    const id = String(g?.id || "").slice(0, 40), name = String(g?.name || "").trim().slice(0, 80);
    if (!id || !name || out.some((x) => x.id === id)) continue;
    out.push({ id, name, max: Math.max(1, Math.min(20, Math.floor(Number(g?.max) || 1))) });
  }
  return out;
}
const groupOfSel = (list: ListEntry[], groups: CareSelectionGroup[], v: string) => { const e = findCare(list, v); return e?.selGroup && groups.some((g) => g.id === e.selGroup) ? e.selGroup : ""; };
const meaningOf = (list: ListEntry[], v: string) => { const e = findCare(list, v); return e ? careMeaning(e) : v; };

/** Symbols in label order: grouped ones first (groups in their order, then each symbol's order in its group), then the
 *  rest A–Z by their wording. */
export function careOrderByGroups(symbols: string[], list: ListEntry[], groups: CareSelectionGroup[]): string[] {
  const gi = (v: string) => { const g = groupOfSel(list, groups, v); return g ? groups.findIndex((x) => x.id === g) : groups.length; };
  const li = (v: string) => { const e = findCare(list, v); return e ? (Number.isFinite(e.selOrder) ? Number(e.selOrder) : 999) * 10000 + list.indexOf(e) : 99999999; };
  return [...new Set(symbols)].sort((a, b) => gi(a) - gi(b) || (gi(a) < groups.length ? li(a) - li(b) : meaningOf(list, a).localeCompare(meaningOf(list, b), "en", { numeric: true, sensitivity: "base" })));
}
/** The first selection-group rule the ticked symbols break, as a sentence, or "". */
export function careGroupConflict(symbols: string[], list: ListEntry[], groups: CareSelectionGroup[]): string {
  for (const g of groups) {
    const inIt = symbols.filter((v) => groupOfSel(list, groups, v) === g.id);
    if (inIt.length > g.max) return `"${g.name}" allows ${g.max} symbol${g.max === 1 ? "" : "s"}, but ${inIt.length} are ticked (${inIt.map((v) => meaningOf(list, v)).join(", ")}).`;
  }
  return "";
}
/** Why a symbol can't be ticked now (its group is full), or "". */
export function careBlockedReason(v: string, ticked: string[], list: ListEntry[], groups: CareSelectionGroup[]): string {
  if (ticked.includes(v)) return "";
  const gid = groupOfSel(list, groups, v);
  const g = groups.find((x) => x.id === gid);
  if (!g) return "";
  const inIt = ticked.filter((t) => groupOfSel(list, groups, t) === gid);
  if (inIt.length < g.max) return "";
  return g.max === 1 ? `Can't combine with ${meaningOf(list, inIt[0])}` : `${g.name} allows ${g.max} – untick one first`;
}
/** The label lines: one per ticked symbol, its Settings wording, in label order. */
export const careWording = (symbols: string[], list: ListEntry[], groups: CareSelectionGroup[]) => careOrderByGroups(symbols, list, groups).map((v) => meaningOf(list, v));
